<template>
  <!-- พิกัด / จุดสังเกต of one job already on คิวรถเล็ก or จัดรถ: a deliveryList
       row (status 'SO') or a `booking` row waiting to be confirmed. Shared by
       carBooking.vue and Delivery/managecar.vue so the two cannot drift -- pins
       have been dropped twice by a hop that wrote its own field list. It reads
       SAP as it opens, writes Firestore through dispatchService.savePin, then
       SAP (pushPinToSap), and emits `saved` for the page to refresh. -->
  <v-dialog :value="value" max-width="560px" :persistent="saving" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="headline">พิกัดและจุดสังเกต</v-card-title>
      <v-card-subtitle v-if="row" class="pt-1">
        <div>{{ row.name || row.custName || '-' }}
          <span v-if="row.soNo">· SO {{ row.soNo }}</span></div>
        <div class="caption">{{ addressText }}</div>
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="suggested" type="info" dense text class="mb-3">
          ดึงจากหมายเหตุในที่อยู่ SAP — ตรวจสอบก่อนบันทึก
        </v-alert>
        <v-alert v-else-if="fromSap" type="info" dense text class="mb-3">
          ดึงจากพิกัด/จุดสังเกตใน SAP — ตรวจสอบก่อนบันทึก
        </v-alert>
        <div v-if="loadingSap" class="caption grey--text mb-3">
          กำลังอ่านพิกัดจาก SAP...
        </div>
        <!-- SAP holds something else: offered, because what is typed here may
             be the correction. -->
        <v-alert v-if="sapUpdate" type="info" dense text class="mb-3">
          <div>ข้อมูลใน SAP ไม่ตรงกับที่กรอกไว้</div>
          <div v-if="sapUpdate.geo" class="caption">พิกัดใน SAP: {{ sapUpdate.geo }}</div>
          <div v-if="sapUpdate.landmark" class="caption">จุดสังเกตใน SAP: {{ sapUpdate.landmark }}</div>
          <v-btn small text color="info" class="mt-1 px-1" @click="applySap">ใช้ค่าจาก SAP</v-btn>
        </v-alert>
        <v-form @submit.prevent="save">
          <v-text-field v-model="geo" label="พิกัด (ละติจูด, ลองจิจูด)"
                        :rules="[geoRule]" clearable color="orange accent-3"
                        placeholder="13.7563, 100.5018" hide-details="auto">
            <template v-slot:append-outer>
              <v-btn small color="orange accent-3" dark @click="openMap">
                <v-icon left small>mdi-map-marker</v-icon>เปิดแผนที่
              </v-btn>
            </template>
          </v-text-field>
          <v-text-field v-model="landmark" label="จุดสังเกต" clearable
                        :counter="landmarkMax" :maxlength="landmarkMax"
                        hint="เช่น บ้านหลังสีฟ้า ตรงข้ามร้านสะดวกซื้อ ปากซอยมีร้านกาแฟ"
                        persistent-hint color="orange accent-3" hide-details="auto" class="mt-3" />
        </v-form>
        <div v-if="row && row.geoUpdatedBy" class="caption grey--text mt-3">
          แก้ไขล่าสุดโดย {{ $personName(row.geoUpdatedBy) }} {{ row.geoUpdatedAt }}
        </div>
        <div v-if="error" class="error--text mt-2">{{ error }}</div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="saving" @click="$emit('input', false)">ยกเลิก</v-btn>
        <v-btn color="orange accent-3" dark :loading="saving" @click="save">บันทึก</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import R from '@/components/bookingRules'
import dispatchService from '@/components/dispatchService'
import {pushPinToSap, pushRtPinToSap, sapFailedNote} from '@/components/sapPin'
import {reportError, reportWarning} from '@/components/report'

const BAD_GEO = 'รูปแบบพิกัดไม่ถูกต้อง เช่น 13.7563, 100.5018'

export default {
  name: 'listPinDialog',
  props: {
    value: Boolean,
    // The deliveryList or booking document, with its `id`.
    row: { type: Object, default: null },
    // The address as the page shows it; also what เปิดแผนที่ searches without a pin.
    address: { type: String, default: '' }
  },
  data () {
    return { geo: '', landmark: '', suggested: false, fromSap: false, sapPin: null,
      loadingSap: false, saving: false, error: '', landmarkMax: R.SAP_LANDMARK_MAX }
  },
  computed: {
    addressText () { return this.address || (this.row && (this.row.address || this.row.custAddress)) || '' },
    // Non-null when the SO in SAP carries a พิกัด or จุดสังเกต the dialog does
    // not -- a shop that corrected the order after this job was dispatched.
    sapUpdate () { return R.sapPinUpdate({ geo: this.geo, landmark: this.landmark }, this.sapPin) }
  },
  watch: {
    value: { immediate: true, handler (open) { if (open && this.row) this.start(this.row) } }
  },
  methods: {
    geoRule (v) { return !v || !!R.parseGeo(v) || BAD_GEO },
    start (row) {
      let geo = row.geo || ''
      let landmark = row.landmark || ''
      let suggested = false
      // Staff write "จุดสังเกต ..." into SAP's address when they have nowhere else
      // to put it, sometimes with a coordinate. Offered, never saved unseen.
      if (!geo && !landmark && row.custAddress) {
        const parsed = R.parseCustAddress(row.custAddress)
        if (parsed.geo || parsed.landmark) {
          geo = parsed.geo
          landmark = parsed.landmark
          suggested = true
        }
      }
      Object.assign(this, { geo, landmark, suggested, fromSap: false, sapPin: null,
        loadingSap: !!row.soNo, saving: false, error: '' })
      // The dialog opens first and the SO is read behind it: a row with a grey
      // pin usually has nothing here to type from, while the order system may
      // already hold the coordinate a shop asked the customer for.
      this.loadSap(row)
    },
    // U_KSK_Geolocation / U_KSK_Landmark for the row's SO. An empty field is
    // filled outright -- sparing the typing is the point -- and a field that
    // disagrees is offered instead, because the value already there may be the
    // corrected one.
    async loadSap (row) {
      if (!R.sapSoNumbers([row.soNo]).length) { this.loadingSap = false; return }
      try {
        const res = await axios.post(`${this.$store.getters.webPath}deliveryWebService.asmx/GetSo`,
          { so: Number(row.soNo) }, { timeout: 10000 })
        const rows = JSON.parse(res.data.d)
        const so = Array.isArray(rows) && rows.length ? rows[0] : null
        // Closed, or moved on to another row, while the service answered.
        if (!so || !this.value || this.row !== row) return
        const sap = R.soPin(so)
        if (sap.geo && !this.geo) { this.geo = sap.geo; this.fromSap = true }
        if (sap.landmark && !this.landmark) { this.landmark = sap.landmark; this.fromSap = true }
        if (this.fromSap) this.suggested = false
        this.sapPin = sap
      } catch (error) {
        // Advisory: the dialog is still usable by hand, and a lookup that failed
        // must not look like an SO with nothing in it.
        reportWarning('Reading พิกัด from SAP failed:', error)
      } finally {
        if (this.row === row) this.loadingSap = false
      }
    },
    applySap () {
      const update = this.sapUpdate
      if (!update) return
      if (update.geo !== undefined) this.geo = update.geo
      if (update.landmark !== undefined) this.landmark = update.landmark
      this.fromSap = true
    },
    openMap () {
      const point = R.parseGeo(this.geo)
      const query = point ? R.formatGeo(point) : this.addressText.trim()
      const url = query
        ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(query)
        : 'https://www.google.com/maps'
      window.open(url, '_blank', 'noopener')
    },
    async save () {
      const raw = (this.geo || '').trim()
      const point = R.parseGeo(raw)
      // Refused, not dropped: a half-parsed coordinate sends a driver somewhere
      // confidently wrong.
      if (raw && !point) { this.error = BAD_GEO; return }
      const row = this.row
      const fields = { geo: point ? R.formatGeo(point) : '', landmark: (this.landmark || '').trim() }
      this.saving = true
      this.error = ''
      try {
        const to = await dispatchService.savePin(row, fields, this.$store.state.user)
        this.$emit('saved', fields)
        this.$emit('input', false)
        // Then SAP, for every SO of the visit -- after Firestore, and never
        // undoing it. Only the fields that changed are sent, measured against
        // what SAP itself answered when the dialog opened where that is known:
        // the row can be older than SAP, and writing a value back that SAP
        // already holds is a write nobody asked for.
        // A pickup has no SO: its pin goes to the RT (RJ.pinNumbers).
        const patch = R.sapPinPatch(this.sapPin || row, fields)
        const webPath = this.$store.getters.webPath
        const failed = (await pushPinToSap(webPath, to.soNos, patch)).concat(await pushRtPinToSap(webPath, to.rtNos, patch))
        if (failed.length) {
          this.$root.$message('อัปเดต SAP ไม่สำเร็จ', sapFailedNote(failed), { type: 'alert', color: 'warning', width: 450 })
        }
      } catch (error) {
        reportError('Saving พิกัด failed:', error)
        this.error = 'บันทึกไม่สำเร็จ โปรดลองอีกครั้ง'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
